import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import HeroSection from './dashboardComponents/HeroSection';
import ToilCard from './dashboardComponents/ToilCard';
import LeaveCompactCard from './dashboardComponents/LeaveCompactCard';
import MyWeekCard, { WorkPattern } from './dashboardComponents/MyWeekCard';
import ExpensesCard from './dashboardComponents/ExpensesCard';
import BirthdaysCard from './dashboardComponents/BirthdaysCard';
import RecentEmployeesCard from './dashboardComponents/RecentEmployeesCard';
import NewsCard from './dashboardComponents/NewsCard';
import FeedCard from './dashboardComponents/FeedCard';
import MiniCalendarCard from './dashboardComponents/MiniCalendarCard';
import MyTrainingCard from './dashboardComponents/MyTrainingCard';
import CloseIcon from '@mui/icons-material/Close';

interface Props { access: any; baseUrl: string; urlLogo?: string; }

interface UserData {
  id: number; first_name: string; last_name: string;
  pattern?: WorkPattern;
  company: { title: string; unit: string; zone_description: string; static_currency?: { iso_alpha3?: string } };
}

// ── Widget registry ────────────────────────────────────────────────────────────

const WIDGET_DEFS = [
  { id: 'toil',      label: 'TOIL Balance' },
  { id: 'leaveCompact', label: 'Absence' },
  { id: 'myWeek',    label: 'My Week' },
  { id: 'training',  label: 'My Training' },
  { id: 'expenses',  label: 'Expenses' },
  { id: 'news',      label: 'Company News' },
  { id: 'feed',      label: 'My feed' },
  { id: 'minical',   label: 'Mini Calendar' },
  { id: 'birthdays', label: 'Upcoming Birthdays' },
  { id: 'recent',    label: 'New Employees' },
] as const;

type WidgetId = typeof WIDGET_DEFS[number]['id'];

// ── Layout types ───────────────────────────────────────────────────────────────

interface LayoutRow {
  id: string;
  cols: 1 | 2 | 3 | 4;
  slots: (WidgetId | null)[];
}

type DragSlot = { rowId: string; slotIdx: number };

// ── Defaults ───────────────────────────────────────────────────────────────────

const DEFAULT_ROWS: LayoutRow[] = [
  { id: 'r1', cols: 3, slots: ['myWeek', 'leaveCompact', 'expenses'] },
  { id: 'r3', cols: 1, slots: ['feed'] },
];
const DEFAULT_SIDEBAR: (WidgetId | null)[] = ['birthdays', 'recent', null];

// ── Layout (server-side, per user: GET/PUT dashboard/layout) ───────────────────

// Removed widgets in saved layouts: replaced by their successor (if not already placed) or emptied
const REPLACED: Record<string, WidgetId> = { week: 'myWeek', leave: 'leaveCompact' };
const KNOWN = new Set<string>(WIDGET_DEFS.map(w => w.id));

function migrateSlots(slots: (string | null)[], placed: Set<string>): (WidgetId | null)[] {
  return slots.map(id => {
    if (!id) return null;
    const next = KNOWN.has(id) ? id : REPLACED[id];
    if (!next || (next !== id && placed.has(next))) return null;
    placed.add(next);
    return next as WidgetId;
  });
}

const defaultRows = (): LayoutRow[] => DEFAULT_ROWS.map(r => ({ ...r, slots: [...r.slots] }));
const defaultSidebar = (): (WidgetId | null)[] => [...DEFAULT_SIDEBAR];

function normalizeLayout(rows: LayoutRow[], sidebar: (string | null)[]) {
  const placed = new Set<string>([...rows.flatMap(r => r.slots), ...sidebar].filter((id): id is string => !!id && KNOWN.has(id)));
  return {
    rows: rows.map(r => ({ ...r, slots: migrateSlots(r.slots, placed) })),
    sidebar: migrateSlots(sidebar, placed),
  };
}

function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

// ── useClickOutside ────────────────────────────────────────────────────────────

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose, active]);
}

// ── WidgetDrop overlay (for empty slots in edit mode) ─────────────────────────

function EmptySlot({ rowId, slotIdx, unplaced, onPlace, isDragOver, onDragOver, onDrop }: {
  rowId: string; slotIdx: number; unplaced: WidgetId[];
  onPlace: (rowId: string, slotIdx: number, id: WidgetId) => void;
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setOpen(false), []);
  useClickOutside(ref, closeMenu, open);

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        border: `2px dashed ${isDragOver ? 'rgba(37,99,235,.6)' : 'rgba(37,99,235,.22)'}`,
        borderRadius: 18,
        background: isDragOver ? 'rgba(37,99,235,.07)' : 'rgba(255,255,255,.32)',
        backdropFilter: 'blur(12px)',
        flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .15s',
      }}
    >
      <div ref={ref} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpen(p => !p)}
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(37,99,235,.1)', border: '1px solid rgba(37,99,235,.2)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, color: '#2563eb', transition: 'background .15s',
          }}
        >+</button>

        {open && (
          <div style={{
            position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 30,
            background: 'var(--nav-bg)', border: '1px solid var(--border-new)',
            borderRadius: 10, padding: 6, minWidth: 180,
            boxShadow: '0 4px 20px var(--shadow2-new)',
            backdropFilter: 'blur(24px)',
          }}>
            {unplaced.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>All widgets placed</div>
            ) : unplaced.map(id => {
              const def = WIDGET_DEFS.find(w => w.id === id);
              return (
                <button key={id} type="button"
                  onClick={() => { onPlace(rowId, slotIdx, id); setOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '7px 11px', border: 'none', background: 'transparent',
                    cursor: 'pointer', fontSize: 12, color: 'var(--text-color)',
                    borderRadius: 7, transition: 'background .12s',
                  }}
                  className="hover:bg-[var(--hover-new)]"
                >{def?.label}</button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── WidgetWrap (filled slot in edit mode) ─────────────────────────────────────

function WidgetWrap({ editMode, dragging, isOver, onDragStart, onDragOver, onDrop, onDragEnd, onRemove, children }: {
  editMode: boolean; dragging: boolean; isOver: boolean;
  onDragStart: () => void; onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void; onDragEnd: () => void; onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      draggable={editMode}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        position: 'relative', opacity: dragging ? 0.35 : 1,
        transition: 'opacity .15s',
        outline: isOver ? '2px solid rgba(37,99,235,.5)' : 'none',
        borderRadius: 18, cursor: editMode ? 'grab' : 'default',
        flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
      }}
    >
      {editMode && (
        <>
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 2, opacity: .4, pointerEvents: 'none' }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 14, height: 2, background: 'var(--text-color)', borderRadius: 1 }} />)}
          </div>
          <button type="button" title="Remove widget" onClick={onRemove}
            style={{ position: 'absolute', top: 6, right: 6, zIndex: 10, width: 22, height: 22, borderRadius: '50%', background: '#ef4444', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
            <CloseIcon style={{ fontSize: 13, color: '#fff' }} />
          </button>
        </>
      )}
      {children}
    </div>
  );
}

// ── Slot height by column count ────────────────────────────────────────────────

const COLS_HEIGHT: Record<1|2|3|4, number> = { 1: 550, 2: 450, 3: 300, 4: 250 };

// ── RowContainer ───────────────────────────────────────────────────────────────

function RowContainer({ row, editMode, dragSrc, dragTgt,
  onChangeRowCols, onRemoveRow,
  onSlotDragStart, onSlotDragOver, onSlotDrop, onSlotDragEnd,
  onClearSlot, onPlaceWidget, unplaced, renderWidget, isHidden }: {
  row: LayoutRow; editMode: boolean;
  dragSrc: DragSlot | null; dragTgt: DragSlot | null;
  onChangeRowCols: (id: string, c: 1|2|3|4) => void;
  onRemoveRow: (id: string) => void;
  onSlotDragStart: (rowId: string, idx: number) => void;
  onSlotDragOver: (e: React.DragEvent, rowId: string, idx: number) => void;
  onSlotDrop: (rowId: string, idx: number) => void;
  onSlotDragEnd: () => void;
  onClearSlot: (rowId: string, idx: number) => void;
  onPlaceWidget: (rowId: string, idx: number, id: WidgetId) => void;
  unplaced: WidgetId[];
  renderWidget: (id: WidgetId) => React.ReactNode;
  isHidden: (id: WidgetId) => boolean;
}) {
  // Widgets the user has no access to render nothing — don't give them a column outside edit mode
  const visible = (id: WidgetId | null) => !!id && !isHidden(id);
  const hasContent = row.slots.some(visible);
  if (!editMode && !hasContent) return null;

  const filledCount = row.slots.filter(visible).length;
  const gridCols = editMode ? row.cols : (filledCount || 1);
  const slotHeight = COLS_HEIGHT[row.cols];

  return (
    <div>
      {/* Row controls */}
      {editMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'var(--space-l1)', paddingLeft: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-color-calendar-week)', marginRight: 2 }}>Cols:</span>
          {([1, 2, 3, 4] as const).map(n => (
            <button key={n} type="button" onClick={() => onChangeRowCols(row.id, n)}
              style={{
                width: 26, height: 26, borderRadius: 7, border: 'none',
                background: row.cols === n ? 'rgba(37,99,235,.15)' : 'var(--surface, rgba(255,255,255,.7))',
                color: row.cols === n ? '#2563eb' : 'var(--text-color-calendar-week)',
                fontWeight: 700, fontSize: 11, cursor: 'pointer',
                boxShadow: row.cols === n ? '0 0 0 1.5px rgba(37,99,235,.4)' : '0 0 0 1px var(--border-new)',
                transition: 'all .15s',
              }}>{n}</button>
          ))}
          <div style={{ flex: 1 }} />
          <button type="button" title="Remove row" onClick={() => onRemoveRow(row.id)}
            style={{
              width: 22, height: 22, borderRadius: '50%',
              background: 'rgba(239,68,68,.1)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#ef4444', fontSize: 13, fontWeight: 700, transition: 'background .15s',
            }}>✕</button>
        </div>
      )}

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: 'var(--space-l1)' }}>
        {row.slots.map((widgetId, slotIdx) => {
          if (!editMode && !visible(widgetId)) return null;

          const isSrc = dragSrc?.rowId === row.id && dragSrc?.slotIdx === slotIdx;
          const isOver = dragTgt?.rowId === row.id && dragTgt?.slotIdx === slotIdx && !isSrc;

          if (!widgetId) {
            return (
              <div key={slotIdx} style={{ height: slotHeight, display: 'flex', flexDirection: 'column' }}>
                <EmptySlot
                  rowId={row.id}
                  slotIdx={slotIdx}
                  unplaced={unplaced}
                  onPlace={onPlaceWidget}
                  isDragOver={isOver}
                  onDragOver={e => onSlotDragOver(e, row.id, slotIdx)}
                  onDrop={() => onSlotDrop(row.id, slotIdx)}
                />
              </div>
            );
          }

          return (
            <div key={slotIdx} style={{ height: slotHeight, display: 'flex', flexDirection: 'column' }}>
              <WidgetWrap
                editMode={editMode}
                dragging={isSrc}
                isOver={isOver}
                onDragStart={() => onSlotDragStart(row.id, slotIdx)}
                onDragOver={e => onSlotDragOver(e, row.id, slotIdx)}
                onDrop={() => onSlotDrop(row.id, slotIdx)}
                onDragEnd={onSlotDragEnd}
                onRemove={() => onClearSlot(row.id, slotIdx)}
              >
                {renderWidget(widgetId)}
              </WidgetWrap>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AddRowButton ───────────────────────────────────────────────────────────────

function AddRowButton({ onAdd }: { onAdd: (cols: 1|2|3|4) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close, open);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(p => !p)}
        style={{
          width: '100%', padding: '10px', borderRadius: 14,
          border: '2px dashed rgba(37,99,235,.22)',
          background: 'rgba(255,255,255,.3)', backdropFilter: 'blur(12px)',
          color: '#2563eb', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          transition: 'all .15s',
        }}
        className="hover:border-[rgba(37,99,235,.5)]"
      >+ Add row</button>

      {open && (
        <div style={{
          position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 30,
          background: 'var(--nav-bg)', border: '1px solid var(--border-new)',
          borderRadius: 12, padding: '12px 16px',
          boxShadow: '0 4px 20px var(--shadow2-new)', backdropFilter: 'blur(24px)',
          display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180,
        }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Choose columns</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {([1, 2, 3, 4] as const).map(n => (
              <button key={n} type="button"
                onClick={() => { onAdd(n); setOpen(false); }}
                style={{
                  flex: 1, height: 38, borderRadius: 10, border: 'none',
                  background: 'rgba(37,99,235,.1)', color: '#2563eb',
                  fontWeight: 800, fontSize: 16, cursor: 'pointer', transition: 'background .15s',
                }}
                className="hover:bg-[rgba(37,99,235,.2)]"
              >{n}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SideWidgetWrap ─────────────────────────────────────────────────────────────

function SideWidgetWrap({ editMode, dragging, isOver, onDragStart, onDragOver, onDrop, onDragEnd, onRemove, children, fill }: {
  editMode: boolean; dragging: boolean; isOver: boolean; fill?: boolean;
  onDragStart: () => void; onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void; onDragEnd: () => void; onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      draggable={editMode}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        position: 'relative', opacity: dragging ? 0.35 : 1,
        transition: 'opacity .15s',
        // last sidebar widget stretches down to the bottom of the main column
        ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' as const } : {}),
        outline: isOver ? '2px solid rgba(37,99,235,.5)' : 'none',
        borderRadius: 16, cursor: editMode ? 'grab' : 'default',
      }}
    >
      {editMode && (
        <>
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 2, opacity: .35, pointerEvents: 'none' }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 12, height: 2, background: 'var(--text-color)', borderRadius: 1 }} />)}
          </div>
          <button type="button" title="Remove" onClick={onRemove}
            style={{ position: 'absolute', top: 6, right: 6, zIndex: 10, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
            <CloseIcon style={{ fontSize: 11, color: '#fff' }} />
          </button>
        </>
      )}
      {children}
    </div>
  );
}

// ── SidebarPanel ───────────────────────────────────────────────────────────────

function SidebarPanel({ ids, editMode, unplaced, dragIdx, dragOverIdx,
  onDragStart, onDragOver, onDrop, onDragEnd,
  onRemove, onAdd, renderWidget }: {
  ids: (WidgetId | null)[];
  editMode: boolean; unplaced: WidgetId[];
  dragIdx: number | null; dragOverIdx: number | null;
  onDragStart: (i: number) => void;
  onDragOver: (e: React.DragEvent, i: number) => void;
  onDrop: (i: number) => void;
  onDragEnd: () => void;
  onRemove: (i: number) => void;
  onAdd: (id: WidgetId) => void;
  renderWidget: (id: WidgetId) => React.ReactNode;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);
  const closeAdd = useCallback(() => setAddOpen(false), []);
  useClickOutside(addRef, closeAdd, addOpen);

  return (
    <div style={{ width: 248, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-l1)' }}>
      {ids.map((widgetId, idx) => {
        if (!widgetId) return null;
        const lastIdx = ids.reduce((l, id, i) => (id ? i : l), -1);
        const isDragging = dragIdx === idx;
        const isOver = dragOverIdx === idx && dragIdx !== idx;
        return (
          <SideWidgetWrap
            key={`${widgetId}-${idx}`}
            editMode={editMode}
            dragging={isDragging}
            isOver={isOver}
            onDragStart={() => onDragStart(idx)}
            onDragOver={e => onDragOver(e, idx)}
            onDrop={() => onDrop(idx)}
            onDragEnd={onDragEnd}
            onRemove={() => onRemove(idx)}
            fill={!editMode && idx === lastIdx}
          >
            {renderWidget(widgetId)}
          </SideWidgetWrap>
        );
      })}

      {/* Add block button — only in edit mode */}
      {editMode && <div ref={addRef} style={{ position: 'relative' }}>
        <button type="button" onClick={() => setAddOpen(p => !p)}
          style={{
            width: '100%', minHeight: 72, padding: '14px',
            border: '2px dashed rgba(37,99,235,.22)',
            borderRadius: 14, background: 'rgba(255,255,255,.28)',
            backdropFilter: 'blur(12px)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
            cursor: 'pointer', transition: 'all .15s',
          }}
          className="hover:border-[rgba(37,99,235,.45)] hover:bg-[rgba(37,99,235,.05)]"
        >
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(37,99,235,.1)', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>＋</div>
          <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', fontWeight: 600 }}>Add block</div>
        </button>

        {addOpen && (
          <div style={{
            position: 'absolute', bottom: '110%', right: 0, zIndex: 30,
            background: 'var(--nav-bg)', border: '1px solid var(--border-new)',
            borderRadius: 10, padding: 6, minWidth: 180,
            boxShadow: '0 4px 20px var(--shadow2-new)', backdropFilter: 'blur(24px)',
          }}>
            {unplaced.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>All widgets placed</div>
            ) : unplaced.map(id => {
              const def = WIDGET_DEFS.find(w => w.id === id);
              return (
                <button key={id} type="button"
                  onClick={() => { onAdd(id); setAddOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '7px 11px', border: 'none', background: 'transparent',
                    cursor: 'pointer', fontSize: 12, color: 'var(--text-color)',
                    borderRadius: 7, transition: 'background .12s',
                  }}
                  className="hover:bg-[var(--hover-new)]"
                >{def?.label}</button>
              );
            })}
          </div>
        )}
      </div>}
    </div>
  );
}

// ── Main Dashboard component ───────────────────────────────────────────────────

const Dashboard: React.FC<Props> = ({ access, baseUrl }) => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [editMode, setEditMode] = useState(false);
  // Role field dashboard_edit_own: customise own layout (backend checks it too)
  const canEditLayout = access?.dashboard_edit_own === true || access?.dashboard_edit_own === 1;
  const [rows, setRows] = useState<LayoutRow[]>(defaultRows);
  const [sidebarIds, setSidebarIds] = useState<(WidgetId | null)[]>(defaultSidebar);

  // Saved layout of this user (none → defaults). The old per-browser copies are dropped.
  const fetchLayout = useCallback(async () => {
    localStorage.removeItem('dashboard_rows_v2');
    localStorage.removeItem('dashboard_sidebar');
    try {
      const { data } = await axios.get('dashboard/layout');
      if (data && Array.isArray(data.rows) && Array.isArray(data.sidebar)) {
        const l = normalizeLayout(data.rows, data.sidebar);
        setRows(l.rows); setSidebarIds(l.sidebar);
      }
    } catch {}
  }, []);

  useEffect(() => { if (auth) fetchLayout(); }, [auth, fetchLayout]);

  // Debounced save of the whole layout; refs hold the latest state when the timer fires
  const rowsRef = useRef(rows); rowsRef.current = rows;
  const sidebarRef = useRef(sidebarIds); sidebarRef.current = sidebarIds;
  const saveTimer = useRef<number | undefined>(undefined);
  const persistLayout = useCallback(() => {
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      axios.put('dashboard/layout', { rows: rowsRef.current, sidebar: sidebarRef.current }).catch(() => {});
    }, 500);
  }, []);
  const saveRows = (_: LayoutRow[]) => persistLayout();
  const saveSidebar = (_: (WidgetId | null)[]) => persistLayout();

  // DnD state — rows
  const [dragSrc, setDragSrc] = useState<DragSlot | null>(null);
  const [dragTgt, setDragTgt] = useState<DragSlot | null>(null);

  // DnD state — sidebar
  const [sidebarDragIdx, setSidebarDragIdx] = useState<number | null>(null);
  const [sidebarDragOver, setSidebarDragOver] = useState<number | null>(null);

  const fetchUser = useCallback(async () => {
    try { const { data } = await axios.get('user'); setUserData(data); } catch {}
  }, []);

  useEffect(() => { if (auth) fetchUser(); }, [auth, fetchUser]);

  // ── Compute unplaced widgets ─────────────────────────────────────────────────

  const unplacedWidgets = (): WidgetId[] => {
    const placed = new Set<WidgetId>();
    rows.forEach(r => r.slots.forEach(s => { if (s) placed.add(s); }));
    sidebarIds.forEach(s => { if (s) placed.add(s); });
    return WIDGET_DEFS.map(w => w.id).filter(id => !placed.has(id));
  };

  // ── Row management ───────────────────────────────────────────────────────────

  const addRow = (cols: 1|2|3|4) => {
    const newRow: LayoutRow = { id: uid(), cols, slots: Array(cols).fill(null) as (WidgetId | null)[] };
    const next = [...rows, newRow];
    setRows(next); saveRows(next);
  };

  const removeRow = (rowId: string) => {
    const next = rows.filter(r => r.id !== rowId);
    setRows(next); saveRows(next);
  };

  const changeRowCols = (rowId: string, newCols: 1|2|3|4) => {
    const next = rows.map(r => {
      if (r.id !== rowId) return r;
      const newSlots: (WidgetId | null)[] = Array(newCols).fill(null);
      for (let i = 0; i < Math.min(r.cols, newCols); i++) newSlots[i] = r.slots[i];
      return { ...r, cols: newCols, slots: newSlots };
    });
    setRows(next); saveRows(next);
  };

  const placeWidget = (rowId: string, slotIdx: number, widgetId: WidgetId) => {
    const next = rows.map(r => {
      if (r.id !== rowId) return r;
      const slots = [...r.slots]; slots[slotIdx] = widgetId;
      return { ...r, slots };
    });
    setRows(next); saveRows(next);
  };

  const clearSlot = (rowId: string, slotIdx: number) => {
    const next = rows.map(r => {
      if (r.id !== rowId) return r;
      const slots = [...r.slots]; slots[slotIdx] = null;
      return { ...r, slots };
    });
    setRows(next); saveRows(next);
  };

  const swapSlots = (src: DragSlot, dst: DragSlot) => {
    let srcVal: WidgetId | null = null, dstVal: WidgetId | null = null;
    rows.forEach(r => {
      if (r.id === src.rowId) srcVal = r.slots[src.slotIdx];
      if (r.id === dst.rowId) dstVal = r.slots[dst.slotIdx];
    });
    const next = rows.map(r => {
      const slots = [...r.slots];
      if (r.id === src.rowId) slots[src.slotIdx] = dstVal;
      if (r.id === dst.rowId) slots[dst.slotIdx] = srcVal;
      return { ...r, slots };
    });
    setRows(next); saveRows(next);
  };

  // ── Sidebar management ───────────────────────────────────────────────────────

  const addToSidebar = (widgetId: WidgetId) => {
    const next = [...sidebarIds, widgetId];
    setSidebarIds(next); saveSidebar(next);
  };

  const removeFromSidebar = (idx: number) => {
    const next = sidebarIds.filter((_, i) => i !== idx);
    setSidebarIds(next); saveSidebar(next);
  };

  const reorderSidebar = (fromIdx: number, toIdx: number) => {
    const next = [...sidebarIds];
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    setSidebarIds(next); saveSidebar(next);
  };

  // ── DnD handlers (rows) ──────────────────────────────────────────────────────

  const handleSlotDragStart = (rowId: string, slotIdx: number) => setDragSrc({ rowId, slotIdx });
  const handleSlotDragOver = (e: React.DragEvent, rowId: string, slotIdx: number) => {
    e.preventDefault(); setDragTgt({ rowId, slotIdx });
  };
  const handleSlotDrop = (rowId: string, slotIdx: number) => {
    if (!dragSrc) return;
    swapSlots(dragSrc, { rowId, slotIdx });
    setDragSrc(null); setDragTgt(null);
  };
  const handleSlotDragEnd = () => { setDragSrc(null); setDragTgt(null); };

  // ── DnD handlers (sidebar) ───────────────────────────────────────────────────

  const handleSidebarDragStart = (idx: number) => setSidebarDragIdx(idx);
  const handleSidebarDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault(); setSidebarDragOver(idx);
  };
  const handleSidebarDrop = (idx: number) => {
    if (sidebarDragIdx === null || sidebarDragIdx === idx) return;
    reorderSidebar(sidebarDragIdx, idx);
    setSidebarDragIdx(null); setSidebarDragOver(null);
  };
  const handleSidebarDragEnd = () => { setSidebarDragIdx(null); setSidebarDragOver(null); };

  // ── renderWidget ─────────────────────────────────────────────────────────────

  // Same access rule as MyTrainingCard (it renders null without it)
  const on = (v: any) => v === true || v === 1;
  const isHidden = (id: WidgetId) =>
    (id === 'training' && !(access?.training_view === true || access?.training_admin === true)) ||
    // own expenses need Finance plus expense or mileage access (same rule as the Finance tabs)
    (id === 'expenses' && !(on(access?.finance_enabled) &&
      [access?.expenses_view, access?.expenses_admin, access?.car_exp_view, access?.car_exp_admin].some(on)));

  const renderWidget = (id: WidgetId): React.ReactNode => {
    switch (id) {
      case 'toil':      return <ToilCard userId={userData?.id || null} auth={auth} />;
      case 'leaveCompact': return <LeaveCompactCard auth={auth} />;
      case 'myWeek':    return <MyWeekCard auth={auth} userId={userData?.id || null} pattern={userData?.pattern} />;
      case 'training':  return <MyTrainingCard auth={auth} access={access} />;
      case 'expenses':  return <ExpensesCard auth={auth} access={access} currency={userData?.company?.static_currency?.iso_alpha3} />;
      case 'news':      return <NewsCard auth={auth} access={access} />;
      case 'feed':      return <FeedCard auth={auth} access={access} currency={userData?.company?.static_currency?.iso_alpha3} baseUrl={baseUrl} />;
      case 'minical':   return <MiniCalendarCard />;
      case 'birthdays': return <BirthdaysCard auth={auth} baseUrl={baseUrl} />;
      case 'recent':    return <RecentEmployeesCard auth={auth} baseUrl={baseUrl} access={access} />;
      default:          return null;
    }
  };

  const unplaced = unplacedWidgets();

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col text-[var(--text-color)]" style={{ gap: 'var(--space-l1)' }}>
      <HeroSection
        firstName={userData?.first_name || ''}
        companyTitle={userData?.company?.title || ''}
        unit={userData?.company?.unit || ''}
        timeZone={userData?.company?.zone_description || null}
        editMode={editMode}
        onToggleEdit={() => setEditMode(e => !e)}
        canEdit={canEditLayout}
      />

      <div style={{ display: 'flex', gap: 'var(--space-l1)', alignItems: 'stretch' }}>

        {/* Left — flexible rows */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-l1)' }}>
          {rows.map(row => (
            <RowContainer
              key={row.id}
              row={row}
              editMode={editMode}
              dragSrc={dragSrc}
              dragTgt={dragTgt}
              onChangeRowCols={changeRowCols}
              onRemoveRow={removeRow}
              onSlotDragStart={handleSlotDragStart}
              onSlotDragOver={handleSlotDragOver}
              onSlotDrop={handleSlotDrop}
              onSlotDragEnd={handleSlotDragEnd}
              onClearSlot={clearSlot}
              onPlaceWidget={placeWidget}
              unplaced={unplaced}
              renderWidget={renderWidget}
              isHidden={isHidden}
            />
          ))}

          {editMode && <AddRowButton onAdd={addRow} />}
        </div>

        {/* Right — fixed sidebar */}
        <SidebarPanel
          ids={sidebarIds}
          editMode={editMode}
          unplaced={unplaced}
          dragIdx={sidebarDragIdx}
          dragOverIdx={sidebarDragOver}
          onDragStart={handleSidebarDragStart}
          onDragOver={handleSidebarDragOver}
          onDrop={handleSidebarDrop}
          onDragEnd={handleSidebarDragEnd}
          onRemove={removeFromSidebar}
          onAdd={addToSidebar}
          renderWidget={renderWidget}
        />
      </div>
    </div>
  );
};

export default Dashboard;
